import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./SenderExpirationTimelineChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardResponsiveChartOptions,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    reverseMonitorDashboardTimelineSeries,
    MONITOR_CHART_GRID_COLOR,
    monitorDashboardDarkerBorder,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SenderExpirationTimelineChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;

    private chart: any = null;
    private canvasId: string = "sender-expiration-timeline-chart-canvas";
    private readonly defaultVisibleMonths: number = 12;
    private readonly timelineMonthCount: number = 72;
    private readonly viewportWidthPx: number = 550;

    public hasData: boolean = true;
    public isLoading: boolean = false;
    public chartCanvasWidthPx: string = "550px";
    private requestVersion: number = 0;
    private monthKeys: string[] = [];
    private chartValues: number[] = [];

    public get chartViewportStyle(): Record<string, string> {
        const showScroll = this.hasData && !this.isLoading;
        return {
            width: "550px",
            maxWidth: "100%",
            height: "100%",
            margin: "0 auto",
            overflowX: showScroll ? "auto" : "hidden",
            overflowY: "hidden",
        };
    }

    public beforeDestroy(): void {
        this.destroyChart();
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(barIndex: number | null): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        const query: any = {};
        if (barIndex != null && this.monthKeys.length > barIndex) {
            const parsed = this.parseMonthKey(this.monthKeys[barIndex]);
            if (parsed) {
                query.chartYear = String(parsed.year);
                query.chartMonth = String(parsed.month);
            }
        }

        this.$router.push({
            name: "SenderExpirationTimelineDetails",
            query: query,
        });
    }

    private onChartClick(evt: any, elements: any[]): void {
        if (!elements || !elements.length) {
            return;
        }

        const index = elements[0]._index != null ? Number(elements[0]._index) : -1;
        if (index < 0) {
            return;
        }

        const count = this.chartValues.length > index ? Number(this.chartValues[index]) : 0;
        if (!count || count <= 0) {
            return;
        }

        this.navigateToDetails(index);
    }

    private parseMonthKey(monthKey: string): { year: number; month: number } | null {
        const parts = String(monthKey).split("-");
        if (parts.length !== 3) {
            return null;
        }

        const month = Number(parts[0]);
        const year = Number(parts[2]);
        if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
            return null;
        }

        return { year: year, month: month };
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const resp = await this.requestsSendersService.GetSendersExpirationTimeline(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.chartCanvasWidthPx = `${this.viewportWidthPx}px`;
            this.destroyChart();
            return;
        }

        const rows =
            resp && resp.result && Array.isArray(resp.result) ? resp.result : [];

        const labels: string[] = [];
        const monthKeys: string[] = [];
        const countsByKey: any = {};

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r || !r.date) continue;
            const key = String(r.date);
            const c = r.count != null ? Number(r.count) : 0;
            countsByKey[key] = (countsByKey[key] ? Number(countsByKey[key]) : 0) + c;
        }

        const now = new Date();
        const timelineStart = new Date(now.getFullYear(), now.getMonth(), 1);
        for (let i = 0; i < this.timelineMonthCount; i++) {
            const d = new Date(timelineStart.getFullYear(), timelineStart.getMonth() + i, 1);
            const key = this.formatMonthKey(d);
            monthKeys.push(key);
            labels.push(this.formatMonthDisplay(d));
        }

        const values: number[] = [];
        for (let i = 0; i < monthKeys.length; i++) {
            const key = monthKeys[i];
            values.push(countsByKey.hasOwnProperty(key) ? Number(countsByKey[key]) : 0);
        }

        reverseMonitorDashboardTimelineSeries(labels, values, monthKeys);

        this.monthKeys = monthKeys.slice();
        this.chartValues = values.slice();

        const total = values.reduce(function (s, x) { return s + x; }, 0);
        this.hasData = total > 0;

        this.chartCanvasWidthPx = this.hasData
            ? this.computeCanvasWidth(labels.length)
            : `${this.viewportWidthPx}px`;

        await this.$nextTick();
        this.renderChart(labels, values);
        this.scrollToOldest(labels.length);
    }

    private renderChart(labels: string[], values: number[]): void {
        this.destroyChart();

        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;

        const yMax = this.computeNiceYAxisMax(values);
        const color = getMonitorDashboardChartColor("senderExpirationTimeline");
        const self = this;
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();
        const fontFamily = getMonitorDashboardFontFamily();

        this.chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "",
                        data: values,
                        backgroundColor: color,
                        borderColor: monitorDashboardDarkerBorder(color),
                        borderWidth: 2,
                        categoryPercentage: 0.7,
                        barPercentage: 0.85,
                    },
                ],
            },
            options: Object.assign({}, baseOpts, {
                onClick: function (evt: any, elements: any[]) {
                    self.onChartClick(evt, elements);
                },
                legend: {
                    display: false,
                },
                scales: {
                    xAxes: [{ gridLines: { display: false } }],
                    yAxes: [
                        {
                            gridLines: {
                                display: true,
                                color: MONITOR_CHART_GRID_COLOR,
                                zeroLineColor: MONITOR_CHART_GRID_COLOR,
                            },
                            scaleLabel: {
                                display: false,
                            },
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                max: yMax,
                                stepSize: this.computeYTickStep(yMax),
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            return String(items[0].xLabel || "");
                        },
                        label: function (item: any, data: any) {
                            const idx = item.index;
                            const lines: string[] = [];
                            let total = 0;
                            for (let i = 0; i < data.datasets.length; i++) {
                                const ds = data.datasets[i];
                                const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                                const value = isNaN(raw) ? 0 : raw;
                                total += value;
                                const dsLabel = ds && ds.label ? String(ds.label) : String(self.$t("shared.senders"));
                                lines.push(dsLabel + ": " + String(value));
                            }
                            if (data.datasets.length > 1) {
                                lines.push(String(self.$t("monitorDashboard.total")) + ": " + String(total));
                            }
                            return lines;
                        },
                    },
                }),
            }),
            plugins: [
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawZeroValueMarkers(chart);
                        self.drawTopValueLabels(chart);
                    },
                },
            ],
        });
    }

    private formatMonthKey(d: Date): string {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${mm}-01-${yyyy}`;
    }

    private computeCanvasWidth(totalMonths: number): string {
        if (totalMonths <= this.defaultVisibleMonths) {
            return `${this.viewportWidthPx}px`;
        }

        const width = Math.ceil((totalMonths / this.defaultVisibleMonths) * this.viewportWidthPx);
        return `${width}px`;
    }

    private scrollToOldest(_totalMonths: number): void {
        const viewport = this.$refs.chartViewport as HTMLElement;
        if (!viewport) return;
        viewport.scrollLeft = 0;
    }

    private formatMonthDisplay(d: Date): string {
        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        const m = months[d.getMonth()] || "";
        const yy = String(d.getFullYear()).slice(-2);
        return `${m} ${yy}`;
    }

    private drawTopValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "bottom";
        ctx2.font = "10px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#6b7280";

        const labelsCount = chart.data && chart.data.labels ? chart.data.labels.length : 0;
        if (labelsCount === 0) {
            ctx2.restore();
            return;
        }

        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data || meta.data.length === 0) {
            ctx2.restore();
            return;
        }

        const yAxis = chart.scales["y-axis-0"];
        const yZero = yAxis ? yAxis.getPixelForValue(0) : 0;

        for (let i = 0; i < labelsCount; i++) {
            const val = Number(chart.data.datasets[0].data[i] || 0);
            const bar = meta.data[i];
            const model = bar._model;
            const x = model.x;

            if (val === 0) {
                ctx2.fillText("0", x, yZero - 6);
            } else {
                const yTop = model.y;
                ctx2.fillText(String(val), x, yTop - 6);
            }
        }

        ctx2.restore();
    }

    private drawZeroValueMarkers(chart: any): void {
        const ctx2: any = chart.ctx;
        const yAxis = chart.scales["y-axis-0"];
        if (!yAxis) return;

        const yZero = yAxis.getPixelForValue(0);
        ctx2.save();
        ctx2.lineWidth = 3;

        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data) {
            ctx2.restore();
            return;
        }

        const strokeColor = chart.data.datasets[0].borderColor;

        for (let i = 0; i < meta.data.length; i++) {
            const val = Number(chart.data.datasets[0].data[i]);
            if (val !== 0) continue;

            const bar = meta.data[i];
            const model = bar._model;
            const x = model.x;
            const halfWidth = Math.max(1, (model.width || 0) / 2);

            ctx2.strokeStyle = strokeColor;
            ctx2.beginPath();
            ctx2.moveTo(x - halfWidth, yZero);
            ctx2.lineTo(x + halfWidth, yZero);
            ctx2.stroke();
        }

        ctx2.restore();
    }

    private computeYTickStep(yMax: number): number {
        if (yMax <= 20) return 5;
        if (yMax <= 100) return 10;
        if (yMax <= 500) return 50;
        return 100;
    }

    private computeNiceYAxisMax(values: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i]);
            if (v > maxVal) maxVal = v;
        }
        if (maxVal <= 0) return 10;

        const step = this.computeYTickStep(maxVal);
        const rounded = Math.ceil(maxVal / step) * step;
        return rounded < step ? step : rounded;
    }

}

